// The priority badge on the ticket page, as a menu: click it to change P1-P4.
// Goes through useTicketAction (set_priority), so the badge changes at once and the
// dispatcher stays the only writer (CLAUDE.md §5). Without permission it is a plain badge.

import { Check, ChevronDown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { priorityColor } from '@/lib/format';
import { useAccess } from '@/lib/use-access';
import { useTicketAction } from '@/lib/use-ticket-action';
import { cn } from '@/lib/utils';

const LEVELS: Array<{ p: string; label: string }> = [
  { p: 'P1', label: 'Highest' },
  { p: 'P2', label: 'High' },
  { p: 'P3', label: 'Normal' },
  { p: 'P4', label: 'Low' },
];

export function PriorityMenu({
  ticket,
}: {
  ticket: { id: string; subject: string | null; priority: string | null; inbox_id: string };
}) {
  const access = useAccess();
  const { run } = useTicketAction();
  const can = access.canIn(ticket.inbox_id, 'tickets.status');

  if (!can) {
    return (
      <Badge variant="pill" className={priorityColor(ticket.priority)}>
        {ticket.priority ?? '—'}
      </Badge>
    );
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="cursor-pointer rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={`Priority ${ticket.priority ?? 'not set'}. Change priority`}
          title="Change priority"
        >
          <Badge variant="pill" className={cn(priorityColor(ticket.priority), 'gap-1')}>
            {ticket.priority ?? '—'}
            <ChevronDown className="h-3 w-3 opacity-60" aria-hidden />
          </Badge>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        {LEVELS.map(({ p, label }) => (
          <DropdownMenuItem
            key={p}
            className="gap-2"
            disabled={p === ticket.priority}
            onSelect={() => void run(ticket.id, 'set_priority', { priority: p }, { subject: ticket.subject })}
          >
            <span className="w-6 font-semibold tabular-nums">{p}</span>
            <span className="flex-1 text-muted-foreground">{label}</span>
            {p === ticket.priority ? <Check className="h-3.5 w-3.5" aria-hidden /> : null}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
